import * as XLSX from 'xlsx';
import { Transaction } from '../../types';

// Helper function to format currency for Excel (as number)
const formatCurrency = (value: number): number => {
    return Math.round(value);
};

// Helper function to get status label
const getStatusLabel = (transaction: Transaction): string => {
    if (transaction.canceled) return 'Cancelado';
    if (transaction.approved) return 'Aprobado';
    return 'Pendiente';
};

// Transform transactions data for Excel export
const transformTransactionsData = (transactions: Transaction[]) => {
    return transactions.map(transaction => {
        // Get payment type name - use custom_payment_name if it's a custom payment type
        const paymentTypeName = Number((transaction as any).payment_type_id) === 22 && (transaction as any).custom_payment_name
            ? (transaction as any).custom_payment_name
            : transaction.payment_type_name;

        return {
            'ID Transacción': transaction.transaction_id || '',
            'ID Externo': transaction.external_id || '',
            'Procesador': transaction.processor_name || '',
            'Usuario': transaction.user_name || '',
            'Tipo de Pago': paymentTypeName || '',
            'ID Tipo Pago Personalizado': (transaction as any).custom_payment_type_id || '',
            'Cliente': transaction.client_name || '',
            'Neto': formatCurrency((transaction as any).net_amount ?? transaction.transaction_amount),
            'Propina': formatCurrency((transaction as any).tip_amount ?? 0),
            'Monto Total': formatCurrency(transaction.transaction_amount),
            'Moneda': transaction.moneda_id || 'CLP',
            'Estado': getStatusLabel(transaction),
            'Aprobado': transaction.approved ? 'Sí' : 'No',
            'Cancelado': transaction.canceled ? 'Sí' : 'No',
            'ID Sesión': (transaction as any).session_id || '',
            'Fecha': transaction.local_timestamp || transaction.timestamp || '',
        };
    });
};

// Main export function
export const exportTransactionsToExcel = (
    transactions: Transaction[],
    fileName?: string
) => {
    try {
        // Transform data
        const transactionsData = transformTransactionsData(transactions);
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(transactionsData);
        
        // Set column widths
        const colWidths = [
            { wch: 15 },  // ID Transacción
            { wch: 20 },  // ID Externo
            { wch: 20 },  // Procesador
            { wch: 20 },  // Usuario
            { wch: 25 },  // Tipo de Pago
            { wch: 20 },  // ID Tipo Pago Personalizado
            { wch: 20 },  // Cliente
            { wch: 15 },  // Neto
            { wch: 15 },  // Propina
            { wch: 15 },  // Monto Total
            { wch: 10 },  // Moneda
            { wch: 12 },  // Estado
            { wch: 10 },  // Aprobado
            { wch: 10 },  // Cancelado
            { wch: 12 },  // ID Sesión
            { wch: 20 },  // Fecha
        ];
        ws['!cols'] = colWidths;
        
        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Transacciones');
        
        // Generate filename
        const timestamp = new Date().toISOString().split('T')[0];
        const finalFileName = fileName || `Transacciones_${timestamp}.xlsx`;
        
        // Write file
        XLSX.writeFile(wb, finalFileName);
        
        console.log(`Excel exported successfully: ${finalFileName}`);
    } catch (error) {
        console.error('Error generating Excel file:', error);
        throw error;
    }
};

// Export filtered transactions by date range
export const exportFilteredTransactions = (
    transactions: Transaction[],
    startDate?: string,
    endDate?: string,
    fileName?: string
) => {
    const timestamp = new Date().toISOString().split('T')[0];
    const dateRange = startDate && endDate 
        ? `${startDate}_a_${endDate}`
        : timestamp;
    
    const finalFileName = fileName || `Transacciones_${dateRange}.xlsx`;
    
    exportTransactionsToExcel(transactions, finalFileName);
};
